import React, {
  useState,
  useEffect,
  createContext,
  useCallback,
  useContext,
  useRef,
  useMemo
} from "react";

import { GET_NOTIFICATIONS, READ_NOTIFICATION } from "./queries";
import { withApollo, useQuery } from "react-apollo";
import { AuthContext } from "../../context/AuthProvider";

export const NotificationsContext = createContext();

const delayedNotificaitonTime = 3 * 60 * 1000; // in milliseconds

const NotificationProvider = ({ children, client }) => {
  const auth = useContext(AuthContext);
  const [delayedShown, setDelayedShown] = useState(() => {
    const raw = localStorage.getItem("shownDelayedNotifications");
    try {
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  });
  const hasShownNotification = useRef(false);

  const delayedPredicate = useCallback(
    n =>
      n.payload.delayed && // is delayed
      !n.status && // is unread
      !delayedShown.some(shown => shown.id === n.id), // hasn't been shown
    [delayedShown]
  );

  const delayablePredicate = useCallback(
    n =>
      n.payload.delayable && // is delayable
      !n.status && // is unread
      !delayedShown.some(shown => shown.id === n.id), // hasn't been shown
    [delayedShown]
  );

  const { data: notificationsGraphQL } = useQuery(GET_NOTIFICATIONS, {
    client,
    variables: {
      id: auth?.user?.id,
      limit: auth.user.adminUser ? 100 : 50,
      offset: 0
    },
    fetchPolicy: "network-only",
    pollInterval: auth?.user?.id ? 2000 : 0,
    skip: !auth?.user?.id
  });

  //update the time after the delay
  const allNotifications = useMemo(() => {
    if (!notificationsGraphQL) return [];
    //this fixes the time for delayed notifications
    return notificationsGraphQL.getNotifications
      .map(n => {
        const wasShown = delayedShown.find(shown => shown.id === n.id);
        if (!wasShown) return n;
        const copy = JSON.parse(
          JSON.stringify({ ...n, created_at: wasShown.created_at })
        );
        return copy;
      })
      .toSorted((a, b) => b.created_at - a.created_at);
  }, [notificationsGraphQL, delayedShown]);
  const showDelayed = useCallback(
    (n, onlyOnce = true) => {
      const copy = JSON.parse(
        JSON.stringify({ ...n, created_at: Date.now().toString() })
      );
      const delayedShownNew = [...delayedShown, copy];
      localStorage.setItem(
        "shownDelayedNotifications",
        JSON.stringify(delayedShownNew)
      );
      setDelayedShown(delayedShownNew);
      if (onlyOnce) hasShownNotification.current = true;
    },
    [delayedShown]
  );
  const delayedNotifications = useMemo(
    () => allNotifications.filter(n => delayedPredicate(n)),
    [allNotifications, delayedPredicate]
  );
  const delayableNotifications = useMemo(
    () => allNotifications.filter(n => delayablePredicate(n)),
    [allNotifications, delayablePredicate]
  );
  const delayeableToDelay = useMemo(
    () =>
      delayableNotifications.reduce(
        (acc, n) => (acc.created_at > n.created_at ? acc : n),
        delayableNotifications?.[0]
      ),
    [delayableNotifications]
  );
  const notificationsWithoutDelay = useMemo(
    () =>
      allNotifications.filter(
        n => !delayedPredicate(n) && n !== delayeableToDelay
      ),
    [allNotifications, delayedPredicate, delayeableToDelay]
  );
  if (delayableNotifications.length > 1) {
    delayableNotifications
      .filter(n => n !== delayeableToDelay)
      .forEach(n => {
        showDelayed(n, false);
      });
  }

  const notifications = useMemo(() => {
    return notificationsWithoutDelay;
  }, [notificationsWithoutDelay]);
  const unread = useMemo(() => {
    return notifications.filter(n => !n.status).length;
  }, [notifications]);

  const fireDelayedNotification = useCallback(() => {
    if (hasShownNotification.current) return;
    const oldestDelayed = delayedNotifications.at(-1);
    if (delayeableToDelay) showDelayed(delayeableToDelay);
    else if (oldestDelayed) {
      showDelayed(oldestDelayed);
    }
  }, [delayedNotifications, delayeableToDelay, showDelayed]);

  //delayed Notification
  useEffect(() => {
    if (hasShownNotification.current) return;
    const timer = setTimeout(
      () => fireDelayedNotification(),
      delayedNotificaitonTime
    );
    return () => {
      clearTimeout(timer);
    };
  }, [fireDelayedNotification]);

  const getNotifications = () => {
    if (auth.user.id) {
      return notificationsWithoutDelay;
    } else return [];
  };

  // eslint-disable-next-line
  const readNotification = useCallback(
    async (_, notificationId) => {
      try {
        await client.mutate({
          mutation: READ_NOTIFICATION,
          variables: { id: notificationId },
          updateQueries: [GET_NOTIFICATIONS]
        });
      } catch (err) {
        console.log(err);
      }
    },
    // eslint-disable-next-line
    [auth.user.id, client]
  );

  const markAllAsRead = async () => {
    // Can't use READ_ALL_NOTIFICATIONS — it would mark delayed notifications as
    // read on the server before they've been shown, breaking that feature.
    try {
      notifications
        .filter(n => !n.status)
        .forEach(n =>
          client.mutate({
            mutation: READ_NOTIFICATION,
            variables: { id: n.id },
            updateQueries: [GET_NOTIFICATIONS]
          })
        );
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        getNotifications,
        readNotification,
        unread,
        markAllAsRead
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export default withApollo(NotificationProvider);
